// A status dropdown that wears the status's own colour.
//
// Ported from Zanetto Tools' StatusSelect (a menu button, not a `<select>`:
// a native select cannot paint its options, and the colour is half of what
// makes a status scannable). The keyboard and focus behaviour below is ZT's
// and is the reason this is a port rather than a fresh component.
//
// The colours are DAYBOARD's semantic set, not ZT's: red stop, green done,
// amber careful, blue here-is-a-fact, grey nothing-yet — the same meanings
// StatusBadge carries, so a status looks the same wherever it appears. No
// orange: orange is the brand accent and means "press this", not "this job
// is in a particular state".
import { useEffect, useRef, useState } from 'react';

export function StatusSelect<T extends string>({
  value,
  options,
  labels,
  colours,
  label,
  disabled,
  onChange,
}: {
  value: T;
  /**
   * The statuses a person may CHOOSE. Deliberately NOT the same list as
   * `labels`: a derived status (a job's `scheduled`) is displayable but not
   * choosable, and it must still read properly on the trigger — which is why
   * the label comes from `labels` and never from this list.
   */
  options: readonly T[];
  /** Every status that can be DISPLAYED, choosable or not. */
  labels: Record<T, string>;
  colours: Record<T, string>;
  /** Names the control for a screen reader: "Status: In progress". */
  label: string;
  disabled?: boolean;
  onChange: (value: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        // Back to the trigger, or focus is left on a menu that no longer
        // exists and Tab restarts from the top of the page.
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${label}: ${labels[value]}`}
        onClick={() => setOpen((was) => !was)}
        className={`flex min-h-11 items-center justify-between gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-9 ${colours[value]}`}
      >
        {labels[value]}
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-max min-w-full overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {options.map((option) => (
            <button
              key={option}
              type="button"
              role="menuitemradio"
              aria-checked={option === value}
              onClick={() => {
                setOpen(false);
                if (option !== value) onChange(option);
              }}
              className={`flex min-h-11 w-full items-center border-0 px-3 py-2.5 text-left text-sm font-medium hover:brightness-95 sm:min-h-0 ${colours[option]}`}
            >
              {labels[option]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Job statuses, painted to match StatusBadge's tones.
 *
 * Round 34: `scheduled` and `in_progress` used to share the same blue, so the
 * two statuses a manager most needs to tell apart at a glance — booked in
 * versus actually happening — were the same chip. Scheduled keeps the blue
 * (it is a derived FACT about the calendar, which is what blue means here);
 * in progress takes teal, which reads as movement without joining the
 * green/amber/red set that already means done, careful and stopped.
 */
/** Quote statuses, on the same semantic set as StatusBadge's QUOTE (round 43). */
export const QUOTE_STATUS_COLOURS = {
  draft: 'bg-gray-100 text-gray-700 border-gray-300',
  sent: 'bg-blue-100 text-blue-800 border-blue-300',
  on_hold: 'bg-amber-100 text-amber-800 border-amber-300',
  won: 'bg-green-100 text-green-800 border-green-300',
  lost: 'bg-red-100 text-red-800 border-red-300',
  expired: 'bg-gray-100 text-gray-600 border-gray-300',
} as const;

export const JOB_STATUS_COLOURS = {
  pending: 'bg-gray-100 text-gray-700 border-gray-300',
  scheduled: 'bg-blue-100 text-blue-800 border-blue-300',
  in_progress: 'bg-teal-100 text-teal-800 border-teal-300',
  // Round 45: amber, as a quote on hold is. Careful, it is parked.
  on_hold: 'bg-amber-100 text-amber-800 border-amber-300',
  completed: 'bg-green-100 text-green-800 border-green-300',
  invoiced: 'bg-purple-100 text-purple-800 border-purple-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300',
} as const;
